import { useCallback, useLayoutEffect, useRef, type MouseEvent as ReactMouseEvent } from "react";

type BoundaryFocusTarget = () => HTMLElement | null;

/**
 * Controls here deactivate themselves at their own boundary (zoom limit, timeline end, already
 * at the nearest frame). The HTML focus fixup rule then blurs the element the user just used and
 * resets keyboard traversal to the document start, so the boundary hands focus onward instead.
 * Only keyboard activation arms it: a pointer user is not navigating the tab ring.
 */
export function useBoundaryFocusRescue() {
  const pendingRef = useRef<{ source: HTMLElement; resolveTarget: BoundaryFocusTarget } | null>(
    null,
  );

  useLayoutEffect(() => {
    const pending = pendingRef.current;
    if (!pending) return;
    pendingRef.current = null;

    const { source, resolveTarget } = pending;
    const deactivated = !source.isConnected || (source as Partial<HTMLButtonElement>).disabled;
    // disabled 처리 직후 source에 초점이 남는 브라우저와 body로 해제하는 브라우저 모두 인계한다.
    // 소비처가 다른 요소로 옮긴 초점은 복구 대상이 아니다.
    const active = document.activeElement;
    if (!deactivated || (active !== source && active !== document.body)) return;
    resolveTarget()?.focus({ preventScroll: true });
  });

  const arm = useCallback((source: HTMLElement, resolveTarget: BoundaryFocusTarget) => {
    if (document.activeElement !== source) return;
    pendingRef.current = { source, resolveTarget };
  }, []);

  /** Delegated on a `role="group"` container so every control in it is covered by one handler. */
  const handleGroupActivation = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      if (event.detail !== 0) return;
      const source = document.activeElement;
      if (!(source instanceof HTMLElement) || !event.currentTarget.contains(source)) return;
      arm(source, () => resolveGroupFocusFallback(source));
    },
    [arm],
  );

  return { arm, handleGroupActivation };
}

/** Nearest still-enabled control after the boundary one, falling back to the closest before it. */
function resolveGroupFocusFallback(source: HTMLElement): HTMLElement | null {
  const group = source.closest<HTMLElement>('[role="group"]');
  if (!group) return null;

  const candidates = Array.from(group.querySelectorAll<HTMLButtonElement>("button:not(:disabled)"));
  const following = candidates.find(
    (candidate) =>
      (source.compareDocumentPosition(candidate) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0,
  );
  return following ?? candidates.at(-1) ?? null;
}
